import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useGlobal, globalizationPath } from '@rcg/ui-react';
import { navigate } from '@gatsbyjs/reach-router/';
import { Typography } from '@rcg/ui-react/lib/components';
import Accordion from '@material-ui/core/Accordion';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionActions from '@material-ui/core/AccordionActions';
import Dictionary from '../../utils/dictionary';
import Button from '../../components/UiComponents/Button';
import useStyles from './index.styles';
import useDictionary from '../../hooks/useDictionary';
import CancellationTable from './ui/CancellationTable';
import HeaderSummary from './ui/HeaderSummary';
import CancellationModal from './ui/CancellationModal';
import DetailsModal from './ui/DetailsModal';
import { getSessionStorageItem } from '../../operations/sessionStorage/utils';
import {
  SESSION_STORAGE_GDS_LOCATOR,
  SESSION_STORAGE_CURRENT_BOOKING,
  SESSION_STORAGE_CURRENT_SAILING,
  SESSION_STORAGE_BOOKING_PROFILE_NAME,
  NAVIGATION_PATHS,
  COOKIE_KEYS,
} from '../../utils/constants';
import { trackCancellationView, trackInteraction } from '../../utils/analytics/browseTracking';
import NotificationBannerComponent from '../../components/NotificationBanner';
import useCommonStyles from '../../utils/useCommonStyles';
import { getCookies } from '../../utils/common';

const getSessionStorageData = () => {
  const profileBooking = getSessionStorageItem(SESSION_STORAGE_BOOKING_PROFILE_NAME);

  const currentBookingSession = getSessionStorageItem(SESSION_STORAGE_CURRENT_BOOKING);
  const currentSailingSession = getSessionStorageItem(SESSION_STORAGE_CURRENT_SAILING) || {};

  const locatorArray = getSessionStorageItem(SESSION_STORAGE_GDS_LOCATOR);

  const isProfileBooking = profileBooking?.flightProfile?.id;
  const flightProfile = isProfileBooking
    ? profileBooking.flightProfile
    : currentBookingSession.flightProfile;
  const currentBookingId = isProfileBooking || currentBookingSession?.id;
  const sailDate = isProfileBooking
    ? profileBooking?.currentBooking?.sailing?.sailDate
    : currentSailingSession?.sailDate;
  const ship = isProfileBooking
    ? profileBooking?.currentBooking?.sailing?.itinerary.ship
    : currentSailingSession?.ship;
  return {
    sailDate,
    ship,
    flightProfile,
    currentBookingId,
    locatorArray,
  };
};
const FlightsCancellation = () => {
  const classes = useStyles();
  const styles = useCommonStyles();
  const getDictionaryValue = useDictionary(Dictionary);
  const {
    data: { market, lang, country },
  } = useGlobal();

  const [cancellationModalOpen, setCancellationModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [cancellationGuests, setCancellationGuests] = useState([]);

  const { sailDate, ship, flightProfile, currentBookingId, locatorArray } = getSessionStorageData();
  const [propNotification, setNotification] = useState({
    description: '',
    open: false,
    title: '',
    type: '',
  });

  const { guests, currency } = flightProfile;

  const guestsByLocators = guests.filter((guest) =>
    guest.flightGuest.gdsLocators.some((locator) => locatorArray.includes(locator)),
  );
  const { guestFlights, airChargesAmount } = guestsByLocators[0].flightGuest || null; // just one guest info whit the same gdsLocator
  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);

  const handleCancellation = (guest) => {
    const { guestId } = guest;
    if (cancellationGuests.some((selectedGuest) => selectedGuest.guestId === guestId)) {
      setCancellationGuests(
        cancellationGuests.filter((selectedGuest) => selectedGuest.guestId !== guestId),
      );
    } else {
      setCancellationGuests([
        ...cancellationGuests,
        {
          ...guest,
          gdsLocators: locatorArray,
        },
      ]);
    }
  };

  const totalAirCharges = cancellationGuests.reduce(
    (total, guest) => total + guest.airChargesAmount,
    0,
  );
  const totalAirPenalty = cancellationGuests.reduce(
    (total, guest) => total + guest.airPenaltyAmount,
    0,
  );

  const handleCloseModal = () => {
    const trakingParams = {
      eventType: 'interaction',
      interaction: 'cancelflight:keepFlight',
    };
    trackInteraction(trakingParams);
    setCancellationModalOpen(false);
  };
  const handleDetailsCloseModal = () => {
    setDetailsModalOpen(false);
  };

  useEffect(() => {
    document.querySelector('.main-container').scrollTo(0, 0);

    const formatConfirmationNumbers = () => {
      const confirmation = guestFlights.flatMap((item) => item.confirmationNumbers);
      const uniqueSet = new Set(confirmation);
      return [...uniqueSet].map((item) => item.replace(/-/g, '')).join('-');
    };
    const loyaltyData = getCookies(COOKIE_KEYS.LOYALTY_DATA);
    const airportCodes = guestFlights?.flatMap((flight) =>
      flight.flightSegments.flatMap((segment) => [
        segment.departure.airport.code,
        segment.arrival.airport.code,
      ]),
    );

    const dataForTracking = {
      country,
      language: lang,
      loginStatus: loyaltyData ? 'Logged In' : 'Anonymous',
      currency,
      sailDate: sailDate || '',
      ship: ship?.code,
      bookingID: currentBookingId,
      flightReservationNumber: formatConfirmationNumbers(),
      flightprodCategory: ['Flight'],
      flightprodSku: airportCodes?.join('-'),
      flightprodQuantity: ['1'],
      flightProdCost: [`${airChargesAmount}`],
    };
    trackCancellationView(dataForTracking);
  }, []);

  const handleContinueCancellation = () => {
    const trakingParams = {
      eventType: 'interaction',
      interaction: 'cancelflight:continue',
    };
    trackInteraction(trakingParams);
    setCancellationModalOpen(true);
  };

  const handleReturnToManage = () => {
    navigate(`${urlPath}${NAVIGATION_PATHS.manage}`);
  };

  return (
    <>
      <Helmet>
        <title>{getDictionaryValue(Dictionary.airCancellationFlightsSeoTitle.key)}</title>
        <meta
          name="description"
          content={getDictionaryValue(Dictionary.airCancellationSeoDescription.key)}
        />
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className={classes.container}>
        <div className={classes.backButtonContainer}>
          <Button
            className="back-button"
            size="small"
            color="primary"
            onClick={handleReturnToManage}>
            <NavigateBeforeIcon />
            {getDictionaryValue(Dictionary.airConfirmationManageFlightsCTA.key)}
          </Button>
        </div>
        <div className={classes.titleContainer}>
          {propNotification.open && (
            <div className={styles.notification}>
              <NotificationBannerComponent
                {...propNotification}
                renderCloseBtn
                isBannerOpen
                onClose={() => setNotification({ open: false })}
              />
            </div>
          )}
          <Typography variant="h5" className={classes.flightsCancellationTitle}>
            {getDictionaryValue(Dictionary.airCancellationTitle.key)}
          </Typography>
          <Typography variant="subtitle1" className={classes.flightsCancellationDescription}>
            {getDictionaryValue(Dictionary.airCancellationDescription.key)}
          </Typography>
        </div>
        <div className={classes.contentContainer}>
          <Accordion square elevation={0} expanded>
            <AccordionSummary
              aria-controls="panel1d-content"
              id="panel1d-header"
              onClick={() => setDetailsModalOpen(true)}>
              <HeaderSummary guestFlights={guestFlights} />
            </AccordionSummary>
            <AccordionDetails>
              <CancellationTable
                guestInformation={guestsByLocators}
                currency={currency}
                handleCancellation={handleCancellation}
                totalAirCharges={totalAirCharges}
                totalAirPenalty={totalAirPenalty}
                cancellationGuests={cancellationGuests}
              />
              <div>
                {cancellationGuests.length === 0 && (
                  <Typography>
                    {getDictionaryValue(Dictionary.airCancellationFooterMessage.key)}
                  </Typography>
                )}
              </div>
            </AccordionDetails>
            <AccordionActions>
              <Button
                data-testid="continue-cancel-flight-btn"
                size="small"
                variant="contained"
                color="primary"
                disabled={!totalAirCharges}
                onClick={() => handleContinueCancellation()}>
                {getDictionaryValue(Dictionary.airCancellationContinueCta.key)}
              </Button>
            </AccordionActions>
          </Accordion>
        </div>
      </div>

      <CancellationModal
        modalOpen={cancellationModalOpen}
        handleCloseModal={handleCloseModal}
        currentBookingId={currentBookingId}
        cancellationGuests={cancellationGuests}
        totalAirPenalty={totalAirPenalty}
        setNotification={setNotification}
      />
      <DetailsModal
        modalOpen={detailsModalOpen}
        handleCloseModal={handleDetailsCloseModal}
        guestsByLocator={guestsByLocators}
      />
    </>
  );
};
export default FlightsCancellation;
